import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

import Confetti from '../../components/Confetti';
import Icon from '../../components/Icon';
import TagPill from '../../components/TagPill';
import WarningGlyph from '../../components/WarningGlyph';
import { notchMask } from '../../components/notch';
import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import {
  formatArea,
  formatTime,
  formatTimeRange,
  goalTooltip,
  longDate,
  timeSlotOf,
} from '../../shared/taxonomy';
import {
  endMinute,
  hasEndTime,
  isMappable,
  LOCATION_LABEL,
  mapsQueryOf,
  rsvpLabel,
  startMinute,
  venueText,
  type Opportunity,
} from '../../shared/opportunity';
import { HALF_MAX, HALF_MIN, PEEK_TOP } from '../../components/HalfSheet';
import { buildDayPlan } from './conflicts';
import { colorFor, type ColorKey } from './colorBy';
import PersonalEventForm from './PersonalEventForm';
import { isPersonal, SLOT_MINUTES, type PersonalDraft } from './personalEvents';
import TravelBlock from './TravelBlock';
import TravelModeDialog from './TravelModeDialog';
import {
  attendanceFor,
  attendedSpan,
  eventEnd as fullEnd,
  eventStart as fullStart,
  snapTrim,
  trimBounds,
  trimLength,
  TRIM_STEP_MINUTES,
  type Trims,
} from './attendance';
import {
  buildTravelLegs,
  clampStart,
  legEnd,
  restartForMode,
  snapToStep,
  travelBounds,
  travelOptions,
  type LegChoices,
  type TravelLeg,
} from './travelLegs';
import type { Home } from './dayRoute';
import {
  COLUMN_HEIGHT,
  DAY_END_MIN,
  DAY_START_MIN,
  HOUR_PX,
  HourGrid,
  HourGutter,
  MINI_HOUR_PX,
  NIGHT_AFTER,
  NIGHT_BEFORE,
  SkyBackdrop,
  SPAN_MIN,
} from './DaySky';
import { useIsMobile } from '../../shared/useIsMobile';
import { trackDrag } from './dragEdge';
import { layoutSpans, seamEnd, type Span } from './layout';
import { type TravelMode } from './travel';
import { CONFLICT, directionsUrl } from './travelStyle';
import { isAccountPath } from '../account/paths';

/**
 * One day, in detail.
 *
 * The column is a scrolling sky from 5am to midnight with dashed hour rules and
 * labels in the gutter. Events are positioned against that grid to scale, so a
 * block's vertical position and height are its real start and duration —
 * otherwise the hour labels would be decoration. Travel estimates sit in the
 * gap they actually occupy, which is what makes a tight connection visible as a
 * cramped space rather than a sentence.
 *
 * Alternatives live in the persistent schedule sidebar, not here.
 *
 * The view is an overlay rather than a replacement: the week stays visible
 * behind an 80% black scrim, with generous margin on both sides. That margin is
 * load-bearing — it is the affordance that tells a user they can click outside
 * to get back to the week — so it survives even at narrow widths.
 */

const GUTTER = 74;

/**
 * The floor on a drawn card, in pixels.
 *
 * Deliberately tiny. It used to be 86 — a readable minimum — which meant a
 * 9:15–9:45 event was drawn as 9:15–10:00 and simply lied about when it ended.
 * A block's position and length are the one thing this view exists to say, so
 * they are now always true, and short cards drop content instead of growing:
 * see `short` and `bare` where the card is rendered. This floor only keeps a
 * zero-length block from vanishing entirely.
 */
const MIN_CARD_H = 6;

/**
 * How much of a card is given up so the next one reads as a separate card.
 * Three minutes is about five pixels at this scale — a seam, not a lie.
 */
const SEAM_MINUTES = 3;

/** Below this a card has no room for the two-button rail or a title. */
const SHORT_CARD_H = 64;
/** Below this only the time fits, whatever the card's width. */
const TINY_CARD_H = 34;

/** Width of the ✕ / list-toggle column at a card's top right. */
const BUTTON_COLUMN = 27;

/** The "I want to" marks: #362f2a at 20% over parchment, on every schedule card. */
const INTENT_COLOR = '#d3cec8';
/**
 * Which list the card is in, as the phone's Explore cards say it.
 *
 * Only on a phone, and only as a statement: the desktop's toggle is a control
 * you can also read, but two stacked 20px targets in a corner of a card that
 * is itself a target is one target, so here the mark just says which list.
 */
const SCHEDULED_MARK = '#4a632c';
const SAVED_MARK = '#47355b';

/** The pin / time-of-day column beside a card's place and hours. */
const META_ICON = 14;

/** Space between two side-by-side cards, and inset from the column's edges. */
const LANE_GAP = 5;

/** One item in the shared layout pass: an event card or a travel block. */
type LaidOut = Span &
  ({ kind: 'event'; event: Opportunity } | { kind: 'leg'; leg: TravelLeg });
/** Smallest readable travel block; below this it is drawn over the next card. */

/**
 * Minutes-after-midnight to pixels down a column of a given height.
 *
 * A function of the height rather than a constant, because on a phone the
 * height is a gesture: pinching the day open and closed is how a small screen
 * gets both "what does my day look like" and "what exactly is at 4:15".
 */
const offsetIn = (height: number) => (minutes: number) =>
  ((Math.min(Math.max(minutes, DAY_START_MIN), DAY_END_MIN) - DAY_START_MIN) / SPAN_MIN) * height;

/**
 * Whatever is actually scrolling around a node, and moving it by a delta.
 *
 * The phone's day sits inside My Schedule's own scrolling box rather than in
 * the page, so `window.scrollBy` moves nothing at all — which is exactly how
 * the first-event scroll and the pinch anchor came to be silent no-ops. This
 * finds the box that really scrolls and falls back to the window for the
 * layouts where the page is the scroller.
 */
function scrollHost(node: HTMLElement | null): HTMLElement | null {
  for (let el = node?.parentElement ?? null; el; el = el.parentElement) {
    const overflow = getComputedStyle(el).overflowY;
    if ((overflow === 'auto' || overflow === 'scroll') && el.scrollHeight > el.clientHeight + 4) {
      return el;
    }
  }
  return null;
}

/** Where the visible day begins on screen: the scroller's top, below any pinned date bar. */
function viewTop(node: HTMLElement, pinned: HTMLElement | null): number {
  const host = scrollHost(node);
  return (host ? host.getBoundingClientRect().top : 0) + (pinned?.offsetHeight ?? 0);
}

function scrollHostBy(node: HTMLElement | null, top: number, smooth = false): void {
  const behavior: ScrollBehavior = smooth ? 'smooth' : 'auto';
  const host = scrollHost(node);
  if (host) host.scrollBy({ top, behavior });
  else window.scrollBy({ top, behavior });
}

/** How long the day takes to scroll a tapped card into place above the half-up callout. */
const CENTER_MS = 900;

/** How far the phone's pinch can take the hour spacing, in pixels per hour. */
const ZOOM_MIN = 46;
const ZOOM_MAX = 210;

/**
 * The phone's day: a panel in the page, not a layer over it.
 *
 * On a wide screen the day is a room you step into and back out of, which is
 * why it has a scrim and arrows. A phone has no room to step into — the week
 * above it is four hundred pixels of thumbnail, and the day is simply what
 * comes next down the page. So it sits in the flow, the page scrolls through
 * it, and leaving is scrolling back up.
 */
const InlineDay = styled('section')({
  position: 'relative',
  marginTop: 14,
  borderTopLeftRadius: 22,
  borderTopRightRadius: 22,
  background: BRAND.mochaDeep,
  /**
   * Deliberately not `overflow: hidden`, however much the rounded corners
   * would like it: hiding overflow makes this a scroll container, and every
   * card's sticky text then sticks to a box that never scrolls. The corners
   * are rounded on the panel and the sky inside it draws its own.
   */
  // The day owns the horizontal axis: across is the swipe to another day.
  touchAction: 'pan-y',
});

/**
 * The phone's date, weekday and counts, pinned while the day scrolls under.
 *
 * The same idea as the sticky dates in Explore: once the week above has
 * scrolled away, the hours alone do not say which day they belong to, and a
 * swipe can change it without the reader noticing. Opaque, unlike Explore's,
 * because here the cards pass directly beneath it.
 *
 * Wider screens get `display: contents` — no box at all, so their layout is
 * exactly what it was.
 */
const DayHeading = styled('div', { shouldForwardProp: (p) => p !== '$pinned' })<{
  $pinned?: boolean;
}>(({ $pinned }) =>
  $pinned
    ? {
        position: 'sticky',
        top: 0,
        // Above every card, handle and travel block in the column.
        zIndex: 30,
        paddingTop: 18,
        background: BRAND.mochaDeep,
        borderTopLeftRadius: 22,
        borderTopRightRadius: 22,
        transition: 'box-shadow 160ms ease',
        // Pinned, the rounded corners would let cards show through them.
        '&[data-stuck="true"]': {
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          boxShadow: '0 12px 16px -14px rgba(0,0,0,0.8)',
        },
      }
    : { display: 'contents' },
);

/** The scrim. Clicking it anywhere returns to the week. */
const Overlay = styled('div')({
  position: 'absolute',
  inset: 0,
  zIndex: 20,
  background: 'rgba(0,0,0,0.8)',
  display: 'flex',
  alignItems: 'stretch',
  justifyContent: 'center',
});

/**
 * Horizontal room on each side of the panel. The arrows live in the middle of
 * this space, and the whole of it is a click target for dismissing the view.
 */
const SIDE_MARGIN = 132;
const NARROW_MARGIN = 64;

const Panel = styled('div', { shouldForwardProp: (p) => p !== '$raised' })<{
  /**
   * Set while the form is open. The form hangs off this panel's left edge, so
   * it can only ever draw inside this panel's stacking context — which means
   * the panel itself has to clear the day arrows (22) and the schedule's own
   * header controls (25) for the form to sit above them.
   */
  $raised?: boolean;
}>(({ theme, $raised }) => ({
  position: 'relative',
  zIndex: $raised ? 26 : 21,
  display: 'flex',
  flexDirection: 'column',
  width: `calc(100% - ${SIDE_MARGIN * 2}px)`,
  maxWidth: 880,
  margin: '18px 0 22px',
  minHeight: 0,
  [theme.breakpoints.down('md')]: {
    width: `calc(100% - ${NARROW_MARGIN * 2}px)`,
  },
}));

const Wrap = styled('div')({
  position: 'relative',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const Head = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  // Left edge tracks the event cards, which start after the hour gutter.
  padding: '4px 8px 14px',
  paddingLeft: GUTTER,
  flexShrink: 0,
  [theme.breakpoints.down('sm')]: { padding: '0 16px 8px', gap: 8, alignItems: 'baseline' },
}));

const Scroller = styled('div', { shouldForwardProp: (p) => p !== '$inline' })<{
  $inline?: boolean;
}>(({ $inline }) => ({
  position: 'relative',
  flex: $inline ? 'none' : 1,
  minHeight: 0,
  // On a phone the day is part of the page, and a box that scrolls inside a
  // page that scrolls is two scrollbars fighting over one thumb.
  overflowY: $inline ? 'visible' : 'auto',
  overscrollBehavior: 'contain',
  borderRadius: 14,
}));

/** Outer scroll content: all-day strip on top, then the timed column. */
const Track = styled('div')({
  position: 'relative',
  paddingLeft: GUTTER,
  paddingRight: 18,
  paddingTop: 14,
  paddingBottom: 60,
});

/**
 * The timed column. The hour rules, the gutter labels and every event block are
 * all positioned inside this one box, which is exactly COLUMN_HEIGHT tall — so
 * they share a single coordinate system and a block always lands on its own
 * hour line.
 */
const Column = styled('div')({
  position: 'relative',
  zIndex: 1,
  // Height is set per render: fixed on a desktop, pinched on a phone.
  // Set by the phone's day swipe, so neighbouring days line up hour for hour.
  marginTop: 'var(--column-lead, 0px)',
});

/**
 * The area the cards live in, inset from the column's own edges.
 *
 * A strip of sky either side, the same width as the gap between two
 * overlapping cards. Without it a single all-day event rendered as one white
 * rectangle filling the screen, with nothing to say it was a card at all.
 */
const Stage = styled('div')({
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: LANE_GAP,
  right: LANE_GAP,
  /**
   * Transparent to the pointer, so a click on empty sky reaches the slot
   * catcher underneath. A positioned box with no background still swallows
   * clicks, and this one covers the whole column — which is why clicking a gap
   * did nothing at all. Everything in here that *is* clickable turns pointer
   * events back on for itself.
   */
  pointerEvents: 'none',
});

/**
 * The event's published hours, when you are attending only part of them.
 *
 * Dashed and empty, exactly as a travel window is — the same idea, so it
 * should look like the same idea. Drawn only when a trim exists, since for
 * everything else it would be an outline around a box it already matches.
 */
const EventWindow = styled('div')({
  position: 'absolute',
  borderRadius: 10,
  border: '2px dashed rgba(243,238,231,0.6)',
  pointerEvents: 'none',
  zIndex: 0,
});

/**
 * The grab points for trimming, at the card's two edges.
 *
 * Hidden until the card is hovered. Nothing on a schedule should advertise a
 * control the reader has not asked for — a day with nine events would carry
 * eighteen dots — but the moment attention is on one card, its edges say what
 * they can do.
 */
const TrimHandle = styled('div', { shouldForwardProp: (p) => p !== '$edge' })<{
  $edge: 'start' | 'end';
}>(({ $edge }) => ({
  position: 'absolute',
  left: '50%',
  [$edge === 'start' ? 'top' : 'bottom']: -6,
  transform: 'translateX(-50%)',
  width: 13,
  height: 13,
  borderRadius: '50%',
  background: BRAND.parchment,
  border: `2px solid ${BRAND.mocha}`,
  cursor: 'ns-resize',
  touchAction: 'none',
  opacity: 0,
  transition: 'opacity 120ms ease',
  zIndex: 5,
}));

/** Everywhere an edge could be dragged to, shown only during the drag. */
const TrimGhost = styled('div')({
  position: 'absolute',
  borderRadius: 10,
  background: 'rgba(243,238,231,0.22)',
  border: '1px dashed rgba(243,238,231,0.55)',
  pointerEvents: 'none',
  zIndex: 1,
});

/**
 * The new-event form, beside the day rather than over it.
 *
 * A dialog would cover the column, and creating an event by clicking a gap is
 * precisely a thing you do while looking at the gap — at what it runs into,
 * and how long you actually have. So the form sits to the left and the
 * schedule stays visible, as a calendar's does.
 */
/**
 * The event being written, drawn where it will land.
 *
 * Dashed and translucent because it does not exist yet — it is a proposal, and
 * should not be mistaken at a glance for something already on the schedule.
 * Both edges drag, so the length can be set against the day rather than by
 * typing times and checking what they collided with.
 */
const DraftBlock = styled('div')({
  position: 'absolute',
  pointerEvents: 'auto',
  left: 0,
  right: 0,
  zIndex: 7,
  borderRadius: 10,
  background: 'rgba(243,238,231,0.72)',
  border: `2px dashed ${BRAND.mocha}`,
  boxSizing: 'border-box',
  padding: '7px 12px',
  color: BRAND.mocha,
  fontFamily: FONTS.body,
  overflow: 'hidden',
  boxShadow: '0 8px 22px rgba(0,0,0,0.32)',
});

/**
 * The phone's draft, written in the sheet below the day: a white outline around
 * half-strength parchment, so it reads as a preview of where the event would go
 * rather than as something already there. No handles: the times are set in the
 * form, which is a thumb's reach away.
 */
const phoneDraft = {
  border: '2px solid #fff',
  background: 'rgba(243,238,231,0.5)',
  boxShadow: 'none',
} as const;

const DraftHandle = styled('div', { shouldForwardProp: (p) => p !== '$edge' })<{
  $edge: 'start' | 'end';
}>(({ $edge }) => ({
  position: 'absolute',
  left: '50%',
  [$edge === 'start' ? 'top' : 'bottom']: -7,
  transform: 'translateX(-50%)',
  width: 15,
  height: 15,
  borderRadius: '50%',
  background: BRAND.parchment,
  border: `2px solid ${BRAND.mocha}`,
  cursor: 'ns-resize',
  touchAction: 'none',
  zIndex: 8,
}));

const SlotCatcher = styled('button')({
  position: 'absolute',
  inset: 0,
  zIndex: 0,
  appearance: 'none',
  border: 'none',
  background: 'transparent',
  padding: 0,
  cursor: 'copy',
});

/**
 * Half again as wide as it started.
 *
 * The date row is three fields, and the first of them holds "Wednesday,
 * September 23" — at 330px that row was a set of boxes too narrow to read what
 * was in them. The panel keeps the extra width as long as there is room beside
 * the day for it, and gives it back below `lg`, where the form sits above the
 * column instead.
 */
const FORM_WIDTH = 495;
/** Narrower than this and the date row is three unreadable boxes again. */
const MIN_FORM_WIDTH = 300;

const FormPanel = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 'calc(100% + 18px)',
  width: FORM_WIDTH,
  maxHeight: '100%',
  overflowY: 'auto',
  zIndex: 24,
  // Tight side padding: every pixel here is a pixel of the date row.
  padding: '20px 20px 22px',
  borderRadius: 16,
  background: '#292420',
  color: BRAND.parchment,
  boxShadow: '0 18px 44px rgba(0,0,0,0.5)',
  // No room beside the column on a narrow screen, so it goes above it.
  [theme.breakpoints.down('lg')]: {
    position: 'static',
    width: 'auto',
    marginBottom: 14,
    maxHeight: 'none',
  },
}));

const Slot = styled('div')({
  position: 'absolute',
  pointerEvents: 'auto',
  display: 'flex',
  flexDirection: 'column',
  '&:hover [data-trim]': { opacity: 1 },
  '&:focus-within [data-trim]': { opacity: 1 },
});

const AllDayStrip = styled('div')({
  position: 'relative',
  zIndex: 2,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  marginBottom: 14,
});

const Card = styled('button', {
  shouldForwardProp: (p) =>
    p !== '$saved' &&
    p !== '$fill' &&
    p !== '$push' &&
    p !== '$ghost' &&
    p !== '$hidden' &&
    p !== '$plain' &&
    p !== '$tight' &&
    p !== '$compact' &&
    p !== '$rail' &&
    p !== '$mini',
})<{
  $saved: boolean;
  $fill: string | null;
  $push?: number;
  /** Extra right padding for the "I want to" marks, when they are shown. */
  $rail?: number;
  /** Hovered alternative: present but clearly not yet committed. */
  $ghost?: boolean;
  /** Mid-reveal: hidden until the scroll lands, then eased in. */
  $hidden?: boolean;
  /** A personal event: a plain rounded box, not a ticket. */
  $plain?: boolean;
  /** A short block: the usual padding is most of its height. */
  $tight?: boolean;
  /**
   * A phone. The rail of buttons on the right shrinks to the one that matters,
   * because every pixel it holds is a pixel of the title on a card that may be
   * a third of a 375px screen wide.
   */
  $compact?: boolean;
  /** In a mini day: the same card at the mini day's scale. */
  $mini?: boolean;
}>(({ $saved, $fill, $push, $ghost, $hidden, $rail, $plain, $tight, $compact, $mini }) => ({
  position: 'relative',
  appearance: 'none',
  cursor: 'pointer',
  width: '100%',
  textAlign: 'left',
  border: 'none',
  background: $fill ?? BRAND.parchment,
  color: BRAND.mocha,
  opacity: $hidden ? 0 : $ghost ? 0.75 : $saved ? 0.5 : 1,
  // Exponential ease-in: slow to start, accelerating to full opacity.
  transition: $hidden
    ? 'none'
    : 'opacity 1000ms cubic-bezier(0.7, 0, 0.84, 0), filter 130ms ease',
  outline: $ghost ? `2px dashed ${BRAND.mocha}` : undefined,
  outlineOffset: $ghost ? -4 : undefined,
  padding: $tight
    ? `${4 + ($push ?? 0)}px ${($compact ? 24 : 34) + ($rail ?? 0)}px 4px ${$compact ? 9 : 12}px`
    : `${11 + ($push ?? 0)}px ${($compact ? 26 : 42) + ($rail ?? 0)}px 12px ${$compact ? 10 : 15}px`,
  height: '100%',
  /**
   * Visible, not hidden, so the content inside can stick.
   *
   * `overflow: hidden` makes an element its own scroll container, and a
   * sticky child then sticks to a box that never scrolls — which is to say,
   * not at all. Nothing escapes anyway: the notch mask below clips painting to
   * the card's own shape whatever the overflow says.
   */
  overflow: 'visible',
  // Content hugs the top, so titles line up across a row of cards however tall
  // each one happens to be.
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'stretch',
  ...($plain
    ? {
        borderRadius: 10,
        // A mask rather than `overflow: hidden`, for the reason above: hidden
        // would make this its own scroll container and kill the sticky text.
        maskImage: 'linear-gradient(#000, #000)',
        WebkitMaskImage: 'linear-gradient(#000, #000)',
        maskRepeat: 'no-repeat',
        WebkitMaskRepeat: 'no-repeat',
      }
    : notchMask({ radius: 10 })),
  '&:hover': { filter: 'brightness(1.04)' },
  ...($mini && {
    padding: `${$tight ? 3 : 5}px ${7 + ($rail ?? 0)}px ${$tight ? 3 : 5}px 7px`,
    ...($plain ? { borderRadius: 5 } : notchMask({ radius: 4.5 })),
  }),
}));

const Remove = styled('button', { shouldForwardProp: (p) => p !== '$push' })<{ $push?: number }>(
  ({ $push }) => ({
  position: 'absolute',
  top: 9 + ($push ?? 0),
  right: 10,
  appearance: 'none',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 4,
  lineHeight: 1,
  fontSize: 15,
  fontWeight: 700,
  color: 'rgba(54,47,42,0.45)',
  borderRadius: 4,
  '&:hover': { color: CONFLICT, background: 'rgba(54,47,42,0.07)' },
}));

/**
 * Which list this card is in, and the one control that moves it.
 *
 * My Schedule shows both lists at once — scheduled at full opacity, saved at
 * half — and opacity alone is a weak signal on a card you are looking at in
 * isolation. This says it outright, and makes it changeable: the resting icon
 * is the filled state of the list the event is in, and hovering previews the
 * other one. So deciding "actually I am going to this" is one click, from the
 * day view, without opening the callout.
 */
const ListToggle = styled('button', { shouldForwardProp: (p) => p !== '$push' })<{
  $push?: number;
}>(({ $push }) => ({
  position: 'absolute',
  // Directly under the ✕, which sits at 9 and stands 23 tall.
  top: 32 + ($push ?? 0),
  right: 10,
  appearance: 'none',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 4,
  lineHeight: 0,
  display: 'flex',
  color: 'rgba(54,47,42,0.45)',
  borderRadius: 4,
  '&:hover': { color: BRAND.mocha, background: 'rgba(54,47,42,0.07)' },
  // The swap is CSS rather than state: it fires on every card in a busy day,
  // and a hover that re-renders React is a hover that can stutter.
  //
  // Both spans are styled from here rather than inline. An inline `display`
  // on the resting icon outranks these rules, which showed both icons stacked
  // on hover instead of exchanging them.
  '& .rest': { display: 'flex' },
  '& .swap': { display: 'none' },
  '&:hover .swap, &:focus-visible .swap': { display: 'flex' },
  '&:hover .rest, &:focus-visible .rest': { display: 'none' },
}));


/**
 * Which list the card is in, on a phone: a mark where the desktop's toggle is.
 *
 * The phone drops the toggle — see the note on `ListToggle` — and until now
 * said "saved for later" in a flag under the hours, which is a line of text
 * for something the Explore cards say with one icon in the corner. This is
 * that icon, in that corner.
 */
const ListMark = styled('div')({
  position: 'absolute',
  // Centred on the ✕'s own centre, which sits 21.5px in from the right.
  top: 32,
  right: 11.5,
  display: 'flex',
  pointerEvents: 'none',
});

/** The icon's check is cut out of its circle; this fills it so the check reads. */
const OnSchedule = styled('span')({
  position: 'relative',
  display: 'block',
  color: SCHEDULED_MARK,
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 4,
    borderRadius: '50%',
    background: BRAND.parchment,
  },
  '& > svg': { position: 'relative' },
});

/**
 * The event's "I want to" marks, stacked under the two buttons at the card's
 * top right — matching "detail day view transport clash.jpg".
 *
 * They sit in the same rail rather than in the card's flow because the flow is
 * the *text*, and the text is top-aligned so a reader can scan titles down the
 * column. Anything in the flow pushes the title around as it appears; anything
 * in the rail cannot.
 */
const IntentRail = styled('div')({
  position: 'absolute',
  // Level with the ✕, and to its left rather than below it: the two buttons
  // are one control group and the marks are not part of it.
  top: 9,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 5,
  // Hoverable, since each mark now names itself. Still out of the card's own
  // click path: the button below stops propagation.
  //
  // A flat colour rather than mocha at half opacity: half of mocha over a
  // pastel lens is a different grey on every card, and these marks should be
  // the same faint grey wherever they land.
  color: INTENT_COLOR,
});

/**
 * How big the "I want to" marks can be on one card.
 *
 * As large as they fit, capped at 57px. Two ceilings bind:
 * the lane's width, since the marks sit beside a 33px button column inside a
 * card that may be one of six; and the card's height, since a stack of three
 * has to clear the bottom edge. Below 13px the artwork stops reading as
 * anything, so it is dropped rather than shrunk further.
 */
const INTENT_MAX = 57;
/**
 * The cap on a phone, where the same marks are decoration and the card is a
 * third of the width. At the desktop cap they took the whole right half of a
 * card and read as its subject, which they are not.
 */
const INTENT_MAX_PHONE = 30;

function intentIconSize(
  lanes: number,
  cardHeight: number,
  count: number,
  mobile: boolean,
): number {
  const max = mobile ? INTENT_MAX_PHONE : INTENT_MAX;
  const byWidth = lanes === 1 ? max : lanes === 2 ? 44 : lanes === 3 ? 30 : 0;
  const byHeight = Math.floor((cardHeight - 18 - (count - 1) * 5) / count);
  return Math.min(byWidth, byHeight, max);
}

/**
 * Keeps a card's text on screen while the card itself scrolls past.
 *
 * The same behaviour as the sticky dates in the Explore list, and for the same
 * reason: a tall event — an all-day summit is nine hours of column — otherwise
 * scrolls its title out of view within a few hundred pixels and leaves the
 * reader looking at an unlabelled expanse of colour. Sticking is bounded by
 * the card, so the text leaves exactly when the card does.
 */
const CardContent = styled('div')({
  position: 'sticky',
  // Below the phone's pinned date bar, which publishes its height; nothing
  // is pinned on wider screens, so there it is zero.
  top: 'var(--day-head, 0px)',
  minWidth: 0,
});

const TrimNote = styled('span')({
  fontFamily: FONTS.body,
  // Beside the hours when it fits; on a line of its own, wrapping, when it doesn't.
  whiteSpace: 'normal',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.01em',
  color: 'rgba(54,47,42,0.7)',
});

const Flag = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  fontFamily: FONTS.body,
  fontSize: 10,
  fontWeight: 700,
  padding: '2px 7px',
  borderRadius: 3,
  background: 'rgba(54,47,42,0.09)',
  color: 'rgba(54,47,42,0.72)',
});

// --- the mini day --------------------------------------------------------------

/**
 * How much of the day a mini day shows: six and a half hours, per the
 * reference, or the event and two hours either side of it when that is more.
 */
const MINI_HOURS = 6.5;
const MINI_MARGIN_HOURS = 2;
/** How long a steady window takes to slide to a hovered event outside it. */
const MINI_GLIDE_MS = 220;

/**
 * The room left of the sky for the hour labels. A mini day hangs them outside
 * the box that holds it, into that box's margin, so that the sky lines up with
 * the text above it — as the callout's WHEN section is drawn.
 */
const MINI_GUTTER = { wide: 44, phone: 36 };

/** The focused event's ring, and the fill of one not yet on the schedule. */
const FOCUS_RING = '#fff';
const FOCUS_FILL = '#cad1d9';

/** The ✕ on a mini day's card: the full card's, at the mini day's scale. */
const MiniRemove = styled('button')({
  position: 'absolute',
  top: 1,
  right: 1,
  appearance: 'none',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '3px 4px',
  lineHeight: 1,
  fontSize: 10,
  fontWeight: 700,
  color: 'rgba(54,47,42,0.45)',
  borderRadius: 3,
  '&:hover': { color: CONFLICT, background: 'rgba(54,47,42,0.07)' },
});

/** A window cut into the column. Never scrolled by hand: it is placed on the event. */
const MiniWindow = styled('div')(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  marginLeft: -MINI_GUTTER.wide,
  [theme.breakpoints.down('sm')]: { marginLeft: -MINI_GUTTER.phone },
}));

const MiniTrack = styled('div')(({ theme }) => ({
  position: 'relative',
  paddingLeft: MINI_GUTTER.wide,
  [theme.breakpoints.down('sm')]: { paddingLeft: MINI_GUTTER.phone },
}));

/** Night past either end of the column, so an early or late event can still sit in the middle. */
const NightCap = styled('div')({
  position: 'absolute',
  left: 0,
  right: 0,
  pointerEvents: 'none',
});

/**
 * The event a mini day is shown for, when it is not on the schedule: a white
 * outline around grey, a proposal rather than a card, as in the reference.
 */
const FocusBlock = styled('div')({
  position: 'absolute',
  zIndex: 2,
  boxSizing: 'border-box',
  padding: '6px 8px',
  borderRadius: 6,
  border: `2.5px solid ${FOCUS_RING}`,
  background: FOCUS_FILL,
  color: BRAND.mocha,
  overflow: 'hidden',
});

const FocusTitle = styled('div')({
  fontFamily: FONTS.body,
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1.22,
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});

/**
 * The card at a mini day's size. Nothing under 9px: the reference draws these
 * at about half that, and a card that cannot be read cannot be chosen.
 */
const MiniTitle = styled('div')({
  // Whole, always: what does not fit the card is cut off at its foot, and the
  // title comes first.
  flexShrink: 0,
  fontFamily: FONTS.serif,
  fontSize: 10,
  fontWeight: 700,
  lineHeight: 1.18,
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});

const MiniLine = styled('div')({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 3,
  marginTop: 2,
  minWidth: 0,
  fontFamily: FONTS.body,
  fontSize: 9,
  fontWeight: 700,
  lineHeight: 1.2,
  letterSpacing: '0.02em',
  whiteSpace: 'nowrap',
  '& > span:last-of-type': { overflow: 'hidden', textOverflow: 'ellipsis' },
});

const MiniIcon = styled('span')({
  display: 'flex',
  flexShrink: 0,
  width: 9,
  height: 9,
  opacity: 0.65,
  '& > svg': { width: '100%', height: '100%' },
});

/**
 * Day arrows sit in the middle of each side margin, vertically centered on the
 * screen — matching "calendar focused background v2.jpg".
 */
const Arrow = styled('button', { shouldForwardProp: (p) => p !== '$side' })<{
  $side: 'left' | 'right';
}>(({ theme, $side }) => ({
  position: 'absolute',
  top: '50%',
  zIndex: 22,
  transform: 'translateY(-50%)',
  [$side]: SIDE_MARGIN / 2 - 21,
  appearance: 'none',
  cursor: 'pointer',
  width: 42,
  height: 42,
  borderRadius: '50%',
  display: 'grid',
  placeItems: 'center',
  background: 'rgba(54,47,42,0.55)',
  border: `1px solid ${BRAND.parchment}`,
  color: BRAND.parchment,
  transition: 'background 130ms ease, transform 130ms ease',
  '&:hover:not(:disabled)': { background: 'rgba(243,238,231,0.18)' },
  '&:active:not(:disabled)': { transform: 'translateY(-50%) scale(0.94)' },
  '&:disabled': { opacity: 0.22, cursor: 'default' },
  [theme.breakpoints.down('md')]: {
    width: 34,
    height: 34,
    [$side]: NARROW_MARGIN / 2 - 17,
  },
}));

interface Props {
  dayKey: string;
  events: Opportunity[];
  savedIds: Set<string>;
  colorBy: ColorKey;
  /** `from` is 'day': a card tapped here, which opens the callout half up below 900px. */
  onOpenEvent: (event: Opportunity, from?: 'day') => void;
  /** Saved travel-block positions and modes, keyed by leg id. */
  legChoices: LegChoices;
  /** How much of each event the user is actually attending. */
  trims: Trims;
  /**
   * An event being considered rather than added: drawn as a ghost and scrolled
   * to. In a mini day, the event the day is shown for, and what it centres on.
   */
  previewEvent?: Opportunity | null;
  /**
   * The same day at a glance: a fixed window into it, centred on
   * `previewEvent`, that shows and opens events but changes nothing. No
   * header, no arrows, no dragging, trimming or ✕, and no slot to click.
   */
  mini?: boolean;
  /** The phone's hour spacing, shared so neighbouring days pinch together. A mini day's, when given. */
  hourPx?: number;
  /**
   * A mini day's least window, in hours: the callout's six and a half by
   * default. It still grows to keep two hours either side of what it is centred on.
   */
  windowHours?: number;
  /**
   * The minute a mini day's window is centred on, when the caller keeps it
   * steady (the day focus, over a hovered card): exactly `windowHours` tall,
   * rather than centred on the event and grown around it.
   */
  windowCentre?: number;
  /** A mini day slides to a new centre rather than jumping. */
  glide?: boolean;
  /** Where the user is staying: the day's first journey starts there and its last ends there. */
  home?: Home | null;
  /**
   * A mini day's way into the full one: a click anywhere in it but on an event
   * opens this day on My Schedule, around the hours the window shows.
   */
  onOpenDay?: (dayKey: string, minute: number) => void;
  /** Opened from a mini day: the full view centres this minute, rather than landing on the first event. */
  landAt?: number | null;

  // The full view's controls. A mini day passes none of them but `onRemove`,
  // which gives its cards their ✕.
  canPrev?: boolean;
  canNext?: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  onClose?: () => void;
  /** The callout is half up over this day, so the day needs room to scroll beneath it. */
  calloutPeek?: boolean;
  onRemove?: (id: string) => void;
  /** Moves an event between the schedule and Save for Later. */
  onToggleList?: (id: string) => void;
  onLegStart?: (id: string, startMinutes: number) => void;
  onLegMode?: (id: string, mode: TravelMode, startMinutes: number) => void;
  onTrim?: (id: string, edge: 'start' | 'end', minutes: number) => void;
  /** The personal event being written, when the form is beside this day. */
  draft?: PersonalDraft | null;
  onDraftChange?: (draft: PersonalDraft) => void;
  onDraftSave?: () => void;
  onDraftCancel?: () => void;
  /** Absent while the draft is new: there is nothing yet to delete. */
  onDraftDelete?: () => void;
  /** A click on empty schedule: the half-hour it landed in. */
  onEmptySlot?: (dayKey: string, minutes: number) => void;
  onEditPersonal?: (event: Opportunity) => void;
  /** Dragging a personal event's own edges. Saved without a toast. */
  onRetimePersonal?: (id: string, startMinutes: number, endMinutes: number) => void;
  /** Held back from render until the view has scrolled to it, then faded in. */
  revealingId?: string | null;
  onRevealDone?: () => void;
  /** Drawn beside the open day for the phone's swipe: never scrolls the page. */
  preview?: boolean;
  /** False when the day was reached by swiping, which keeps the page where it is. */
  landOnOpen?: boolean;
  onHourPx?: (px: number) => void;
}

const NOOP = () => {};

/** The phone's read-only version of the toggle above: which list, and nothing else. */
function ListMarkFor({ event, saved }: { event: Opportunity; saved: boolean }) {
  return (
    <ListMark
      role="img"
      aria-label={`${saved ? 'Saved for later' : 'On My Schedule'}: ${event.name}`}
    >
      {saved ? (
        <Box component="span" sx={{ display: 'block', color: SAVED_MARK }}>
          <Icon name="ui:saveOn" size={20} />
        </Box>
      ) : (
        <OnSchedule>
          <Icon name="ui:scheduleOn" size={20} />
        </OnSchedule>
      )}
    </ListMark>
  );
}

/**
 * Renders the toggle for one card. `saved` decides both the resting icon and
 * what the click does — the two are always opposites, so one flag drives both.
 */
function ListToggleButton({
  event,
  saved,
  onToggle,
  push,
}: {
  event: Opportunity;
  saved: boolean;
  onToggle: (id: string) => void;
  push?: number;
}) {
  const label = saved ? 'Add to My Schedule' : "Move to 'Save for Later'";

  return (
    <Tooltip title={label} placement="top" arrow>
      <ListToggle
        $push={push}
        aria-label={`${label}: ${event.name}`}
        onClick={(ev) => {
          ev.stopPropagation();
          onToggle(event.id);
        }}
      >
        <span className="rest">
          <Icon name={saved ? 'ui:saveOn' : 'ui:scheduleOn'} size={saved ? 13 : 15} />
        </span>
        <span className="swap">
          <Icon name={saved ? 'ui:schedule' : 'ui:save'} size={saved ? 15 : 13} />
        </span>
      </ListToggle>
    </Tooltip>
  );
}

export default function DayDetail({
  dayKey,
  events,
  savedIds,
  colorBy,
  onOpenEvent,
  legChoices,
  trims,
  previewEvent,
  mini = false,
  hourPx: sharedHourPx,
  windowHours = MINI_HOURS,
  windowCentre,
  glide = false,
  home = null,
  onOpenDay,
  landAt = null,
  canPrev = false,
  canNext = false,
  onPrev = NOOP,
  onNext = NOOP,
  onClose = NOOP,
  calloutPeek = false,
  onRemove: onRemoveProp,
  onToggleList = NOOP,
  onLegStart = NOOP,
  onLegMode = NOOP,
  onTrim = NOOP,
  draft = null,
  onDraftChange = NOOP,
  onDraftSave = NOOP,
  onDraftCancel = NOOP,
  onDraftDelete,
  onEmptySlot = NOOP,
  onEditPersonal = NOOP,
  onRetimePersonal = NOOP,
  revealingId = null,
  onRevealDone = NOOP,
  preview = false,
  landOnOpen = true,
  onHourPx,
}: Props) {
  const mobile = useIsMobile();
  const onRemove = onRemoveProp ?? NOOP;

  /**
   * Pixels per hour, and everything measured against them.
   *
   * A phone pinches this between a whole day at a glance and an hour worth
   * reading; every other screen keeps the fixed spacing the design was drawn
   * at. `offset` is rebuilt with it, so a card, a travel block, an hour rule
   * and the gutter all move together and cannot drift apart.
   */
  const [ownHourPx, setOwnHourPx] = useState(mobile ? 74 : HOUR_PX);
  const hourPx = sharedHourPx ?? ownHourPx;
  const setHourPx = onHourPx ?? setOwnHourPx;
  const columnHeight = mini
    ? (SPAN_MIN / 60) * (sharedHourPx ?? MINI_HOUR_PX)
    : mobile
      ? (SPAN_MIN / 60) * hourPx
      : COLUMN_HEIGHT;
  const offset = useMemo(() => offsetIn(columnHeight), [columnHeight]);

  const column = useRef<HTMLDivElement | null>(null);
  /** The live spacing, for handlers that must not re-bind on every change. */
  const liveHour = useRef(hourPx);
  liveHour.current = hourPx;
  /** The minute held still while the column grows or shrinks under it. */
  const anchor = useRef<{ minutes: number; screenY: number } | null>(null);

  /**
   * Two fingers set the hour spacing, as they do on every map.
   *
   * Native touch listeners rather than pointer events: two fingers is one
   * gesture, and reassembling it from separate pointers is how pinch handlers
   * end up fighting the browser. Clamped at both ends — below the floor the
   * hour rules collide, above the ceiling a day is a mile of scrolling.
   */
  useEffect(() => {
    const node = column.current;
    if (!node || !mobile || mini) return;

    let span = 0;
    let from = liveHour.current;
    const gap = (t: TouchList) =>
      Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 2) return;
      span = gap(e.touches);
      from = liveHour.current;
      // Whatever is under the middle of the screen stays under it.
      const box = node.getBoundingClientRect();
      const screenY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      anchor.current = {
        minutes: DAY_START_MIN + ((screenY - box.top) / box.height) * SPAN_MIN,
        screenY,
      };
    };

    const onMove = (e: TouchEvent) => {
      if (e.touches.length !== 2 || !span) return;
      e.preventDefault();
      const next = Math.min(Math.max((from * gap(e.touches)) / span, ZOOM_MIN), ZOOM_MAX);
      setHourPx(next);
    };

    const onEnd = () => {
      span = 0;
      anchor.current = null;
    };

    node.addEventListener('touchstart', onStart, { passive: true });
    node.addEventListener('touchmove', onMove, { passive: false });
    node.addEventListener('touchend', onEnd);
    node.addEventListener('touchcancel', onEnd);
    return () => {
      node.removeEventListener('touchstart', onStart);
      node.removeEventListener('touchmove', onMove);
      node.removeEventListener('touchend', onEnd);
      node.removeEventListener('touchcancel', onEnd);
    };
  }, [mobile, mini, setHourPx]);

  /** Holds the pinched-on time in place while the column resizes around it. */
  useLayoutEffect(() => {
    const node = column.current;
    const held = anchor.current;
    if (!node || !held) return;
    const box = node.getBoundingClientRect();
    const nowY = box.top + ((held.minutes - DAY_START_MIN) / SPAN_MIN) * box.height;
    scrollHostBy(node, nowY - held.screenY);
  }, [columnHeight]);

  const scroller = useRef<HTMLDivElement | null>(null);
  const overlay = useRef<HTMLDivElement | null>(null);
  const formPanel = useRef<HTMLDivElement | null>(null);
  const panel = useRef<HTMLDivElement | null>(null);

  /**
   * The form takes its full width when there is room for it beside the day,
   * and whatever is left when there is not.
   *
   * It hangs off the panel's left edge, so "room" is however far that edge is
   * from the window's — a figure that changes with the window, with the filter
   * sidebar, and with the panel's own maximum width. Measured rather than
   * guessed, and only above `lg`, below which the form sits above the column
   * and takes the full width of it.
   */
  const theme = useTheme();
  const beside = useMediaQuery(theme.breakpoints.up('lg'));
  const [formWidth, setFormWidth] = useState(FORM_WIDTH);

  useLayoutEffect(() => {
    const node = panel.current;
    if (!node) return;

    const measure = () => {
      // 18px of gap to the panel, and 10px of air at the window's edge.
      const room = node.getBoundingClientRect().left - 28;
      setFormWidth(Math.max(Math.min(FORM_WIDTH, room), MIN_FORM_WIDTH));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);
  /** Null until the reveal scroll finishes; then the card fades in. */
  const [revealed, setRevealed] = useState(false);
  const [revealBurst, setRevealBurst] = useState(0);

  /**
   * The wheel anywhere over the day scrolls the day.
   *
   * The column is a box in the middle of a dark screen, and a wheel over the
   * scrim beside it did nothing — or worse, scrolled the week grid still
   * sitting behind the scrim. Everything inside the overlay now feeds the
   * column, except the two things that scroll on their own: the column itself
   * and the event form. The filter sidebar is outside the overlay entirely and
   * keeps its own wheel.
   *
   * Registered by hand rather than with `onWheel` because React's wheel
   * listeners are passive, and this one has to call `preventDefault` to stop
   * the week behind from scrolling too.
   */
  useEffect(() => {
    const node = overlay.current;
    if (!node) return;

    const onWheel = (event: WheelEvent) => {
      const column = scroller.current;
      if (!column) return;

      const target = event.target as Node | null;
      if (target && (column.contains(target) || formPanel.current?.contains(target))) return;

      event.preventDefault();
      // Firefox reports lines, and some setups report pages.
      const lines = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? column.clientHeight : 1;
      column.scrollTop += event.deltaY * lines;
    };

    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
  }, []);

  /**
   * Below 900px a tapped card opens the callout half up, and the day scrolls so
   * the card sits in the middle of what is left above it: between the date bar
   * and the callout's top edge, or just under the date bar if the card is taller
   * than that space.
   *
   * The target is measured again on every frame rather than once, because the
   * header and the row of buttons are sliding away over the same few hundred
   * milliseconds, and each frame of that moves the day. A touch or a wheel on
   * the day hands the scroll back to the reader.
   */
  const narrow = useMediaQuery(theme.breakpoints.down('md'));
  const centerAboveCallout = useCallback(
    // `peekTop` is the sheet the card is being centred above, as a share of the
    // screen: the form's, when the draft asks. Otherwise the callout, whose
    // height is measured off its contents, so its top is read off the sheet
    // (`data-half-top`); until it has risen, the least it shows.
    (card: HTMLElement, peekTop?: number) => {
      if (!narrow || preview) return;
      const host = mobile ? scrollHost(card) : scroller.current;
      if (!host) return;

      const from = host.scrollTop;
      const began = performance.now();
      let frame = 0;
      let settling = 0;
      const stop = () => {
        cancelAnimationFrame(frame);
        host.removeEventListener('wheel', stop);
        host.removeEventListener('touchstart', stop);
      };

      const target = () => {
        const box = card.getBoundingClientRect();
        const top = Math.max(host.getBoundingClientRect().top, mobile ? (pinned.current?.getBoundingClientRect().bottom ?? 0) : 0);
        const callout = document.querySelector<HTMLElement>('[data-half-top]')?.dataset.halfTop;
        const bottom =
          peekTop !== undefined
            ? window.innerHeight * peekTop
            : callout
              ? Number(callout)
              : window.innerHeight * (1 - HALF_MIN);
        const delta =
          box.height > bottom - top - 24
            ? box.top - (top + 12)
            : box.top + box.height / 2 - (top + bottom) / 2;
        return Math.max(0, Math.min(host.scrollHeight - host.clientHeight, host.scrollTop + delta));
      };

      const step = (now: number) => {
        // Slow and even: easing in and out over nearly a second, so the day is
        // seen to travel rather than jump.
        const t = Math.min(1, (now - began) / CENTER_MS);
        const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        const goal = target();
        host.scrollTop = from + (goal - from) * eased;
        // A few more frames once the curve is done, for the last of the layout to settle.
        if (t < 1 || (Math.abs(goal - host.scrollTop) > 1 && settling++ < 12)) frame = requestAnimationFrame(step);
        else stop();
      };

      host.addEventListener('wheel', stop, { passive: true, once: true });
      host.addEventListener('touchstart', stop, { passive: true, once: true });
      frame = requestAnimationFrame(step);
    },
    [narrow, preview, mobile],
  );

  /**
   * The phone's draft, kept in the middle of the space above the form's sheet:
   * when it is first drawn, and each time its day or hours change. Typing a
   * title leaves the day where it is.
   */
  const draftBlock = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!mobile || !draft || !draftBlock.current) return;
    centerAboveCallout(draftBlock.current, PEEK_TOP);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobile, draft?.id, draft?.dayKey, draft?.startMinutes, draft?.endMinutes]);

  /** Scrolls an event to the middle of the column, for the reveal sequence. */
  const centerOn = useCallback((event: Opportunity, smooth: boolean) => {
    const node = scroller.current;
    const start = startMinute(event);
    if (!node || start == null) return;
    const mid = (start + (endMinute(event) ?? start + 60)) / 2;
    node.scrollTo({
      top: Math.max(0, offset(mid) - node.clientHeight / 2 + 18),
      behavior: smooth ? 'smooth' : 'auto',
    });
  }, [offset]);


  /**
   * A hovered alternative is drawn into the day as a ghost so the user can see
   * where it would land before committing to it.
   */
  const shown = useMemo(() => {
    // On a phone, a personal event being edited is drawn once: as the draft.
    const listed = mobile && draft ? events.filter((e) => e.id !== draft.id) : events;
    const base = previewEvent && !listed.some((e) => e.id === previewEvent.id)
      ? [...listed, previewEvent]
      : listed;
    return base;
  }, [events, previewEvent, mobile, draft]);

  /**
   * Overlaps are scanned on attended times, not published ones: an event you
   * are leaving at noon does not clash with the one that starts at noon.
   */
  const plan = useMemo(
    () => buildDayPlan(dayKey, shown, (e) => attendedSpan(e, trims)),
    [dayKey, shown, trims],
  );

  // Hovering an alternative brings its slot into view. A mini day is already
  // centred on it, and has nothing to scroll.
  useEffect(() => {
    if (previewEvent && !mini) centerOn(previewEvent, true);
  }, [previewEvent, centerOn, mini]);

  /**
   * The arrival sequence: scroll the new event to center, wait for the scroll to
   * settle, then fade the card in and fire the confetti.
   *
   * `onRevealDone` is held in a ref rather than listed as a dependency. The
   * parent passes an inline arrow, so a fresh identity on every render would
   * re-run this effect continuously — each run clearing the previous timeout
   * before it could fire, and resetting the scroll to the top.
   */
  const revealDone = useRef(onRevealDone);
  useEffect(() => {
    revealDone.current = onRevealDone;
  }, [onRevealDone]);

  const revealTarget = revealingId
    ? (events.find((e) => e.id === revealingId) ?? null)
    : null;
  const revealKey = revealTarget ? `${revealTarget.id}:${startMinute(revealTarget)}` : '';

  useEffect(() => {
    if (!revealKey || !revealTarget) {
      setRevealed(false);
      return;
    }

    setRevealed(false);
    // One frame's grace so the column has laid out before we measure it.
    const raf = requestAnimationFrame(() => centerOn(revealTarget, true));
    const t = setTimeout(() => {
      setRevealed(true);
      setRevealBurst((n) => n + 1);
      setTimeout(() => revealDone.current(), 1200);
    }, 750);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
    // revealKey captures the identity that matters; revealTarget is derived.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealKey, centerOn]);

  /**
   * Travel gaps, plus how far each following card has to push its content down.
   *
   * A tight connection has almost no room between the two events, so the block
   * would render as an unreadable sliver. Instead it is given a readable
   * minimum height and drawn over the next card. The card keeps its exact
   * position on the grid — only its inner content and its ✕ move down, so the
   * block never covers a title.
   */
  /**
   * One layout pass over events and travel blocks together.
   *
   * They have to share it: a journey that runs into the next event is drawn
   * beside it rather than over it, and that only works if both are competing
   * for the same lanes. Laid out on true times, which is also what is drawn:
   * two events that do not overlap must not be laned as though they did.
   */
  /**
   * The edge being dragged, and the range it may land in. Held in state rather
   * than a ref because the ghost is drawn from it.
   */
  const [trimDrag, setTrimDrag] = useState<{
    id: string;
    min: number;
    max: number;
  } | null>(null);

  /** The travel block whose mode dialog is open, if any. */
  const [openLegId, setOpenLegId] = useState<string | null>(null);

  const { placed, legsById, pxPerMinute, windowLanes, overlappingIds, startsAt } = useMemo(() => {
    const scale = columnHeight / SPAN_MIN;
    const timedEvents = plan.timed.map((t) => t.event);
    const legs = buildTravelLegs(dayKey, timedEvents, legChoices, (e) => attendedSpan(e, trims), home);

    const spans: LaidOut[] = [
      /**
       * Laid out on the hours being attended rather than the published ones.
       * Two events that were double-booked and are no longer — because one of
       * them is now being left early — should stop sharing the column, which
       * is the visible half of "that clash is resolved".
       */
      ...timedEvents.map((event) => ({
        id: event.id,
        ...attendedSpan(event, trims),
        kind: 'event' as const,
        event,
      })),
      ...legs.map((leg) => ({
        id: leg.id,
        start: leg.startMinutes,
        end: legEnd(leg),
        kind: 'leg' as const,
        leg,
      })),
    ];

    /**
     * The same layout over published windows: where each card *was* before any
     * trimming. A card released from a lane keeps the side it was released
     * from, so resolving a clash never makes two cards swap places.
     */
    const windows = new Map<string, { lane: number; lanes: number }>();
    for (const p of layoutSpans(
      timedEvents.map((event) => ({ id: event.id, start: fullStart(event), end: fullEnd(event) })),
    )) {
      windows.set(p.item.id, { lane: p.lane, lanes: p.lanes });
    }

    /** Every attended start, so a card can find whatever begins where it ends. */
    const startsAt = spans.filter((s) => s.kind === 'event').map((s) => s.start);

    const clashing = new Set<string>();
    for (const [a, b] of plan.overlaps) {
      clashing.add(a.id);
      clashing.add(b.id);
    }

    return {
      // Events first, so travel blocks take the rightmost lane and stay there
      // while one is being dragged past an event's start.
      placed: layoutSpans(spans, (item) => (item.kind === 'leg' ? 1 : 0)),
      legsById: new Map(legs.map((l) => [l.id, l])),
      pxPerMinute: scale,
      windowLanes: windows,
      overlappingIds: clashing,
      startsAt,
    };
  }, [plan, dayKey, legChoices, trims, columnHeight, home]);
  const openLeg = openLegId ? (legsById.get(openLegId) ?? null) : null;

  /** A placed item's share of the column's width, less the gap between lanes. */
  const laneOf = (entry: (typeof placed)[number]) => {
    const width = 100 / entry.lanes;
    return {
      left: `calc(${entry.lane * width}% + ${entry.lane === 0 ? 0 : LANE_GAP}px)`,
      width: `calc(${width}% - ${entry.lanes > 1 ? LANE_GAP : 0}px)`,
    };
  };

  /** Where one event's card, and its published hours, sit in the column. */
  const placeEvent = (entry: (typeof placed)[number], event: Opportunity) => {
    /**
     * A personal event is never partly attended. There is no published event
     * to arrive late to — the block *is* the user's plan — so a stored trim is
     * ignored rather than applied, and the edges move the event itself instead.
     */
    const personal = isPersonal(event);
    const attend = attendanceFor(event, personal ? undefined : trims[event.id]);

    /**
     * A card that shared the column until someone trimmed the event beside
     * it, and now has the hours to itself again.
     *
     * It gets most of its width back, but not all: the other event's published
     * window is still drawn where this card now sits, and a full-width card
     * would paint over the one thing that says "you are leaving that early".
     * Eighty percent, justified to the side it was already on, leaves the
     * window showing beside it.
     *
     * Never the trimmed card itself. That one is the answer to the question,
     * and it takes the full width.
     */
    let lane = laneOf(entry);
    const wasLaned = windowLanes.get(event.id);
    const released =
      entry.lanes === 1 && !attend.trimmed && wasLaned !== undefined && wasLaned.lanes > 1;
    if (released) {
      lane = wasLaned.lane === 0 ? { left: '0', width: '80%' } : { left: '20%', width: '80%' };
    }
    const windowTop = offset(fullStart(event));
    const windowHeight = Math.max(offset(fullEnd(event)) - windowTop, MIN_CARD_H);
    const cardTop = offset(attend.start);
    // Drawn a few minutes short when something starts where this ends, so the
    // two cards do not read as one.
    const height = Math.max(offset(seamEnd(attend.end, startsAt, SEAM_MINUTES)) - cardTop, MIN_CARD_H);

    return {
      personal,
      attend,
      overlapping: overlappingIds.has(event.id),
      lane,
      windowTop,
      windowHeight,
      cardTop,
      height,
    };
  };

  /**
   * Clamped before it is written, not only when it is read.
   *
   * `buildTravelLegs` clamps on read too, so the rendering is safe either way —
   * but storing a departure outside the window means a later change to the
   * surrounding events could resurrect a position the user never chose.
   */
  const onLegDrag = useCallback(
    (id: string, startMinutes: number) => {
      const leg = legsById.get(id);
      if (!leg) return;
      onLegStart(
        id,
        clampStart(snapToStep(startMinutes), leg.windowStart, leg.windowEnd, leg.estimate.minutes),
      );
    },
    [legsById, onLegStart],
  );

  /**
   * Switching mode keeps the arrival time and moves the departure. The fixed
   * point in a journey is when you have to be there; a faster mode should mean
   * leaving later, not arriving early.
   */
  const onModeChange = useCallback(
    (mode: TravelMode) => {
      if (!openLeg) return;
      const option = travelOptions(openLeg.from, openLeg.to).find((o) => o.mode === mode);
      if (!option) return;
      onLegMode(openLeg.id, mode, restartForMode(openLeg, option.minutes));
    },
    [openLeg, onLegMode],
  );

  /**
   * Dragging one edge of a card.
   *
   * Reads the pixel-per-minute scale from the same place the layout does, so a
   * drag of one hour's height moves the edge by exactly an hour whatever the
   * column is scaled to.
   */
  const beginTrim = useCallback(
    (
      e: React.PointerEvent<HTMLDivElement>,
      event: Opportunity,
      edge: 'start' | 'end',
      attend: { start: number; end: number },
    ) => {
      e.preventDefault();
      e.stopPropagation();

      const other = edge === 'start' ? attend.end : attend.start;
      const { min, max } = trimBounds(event, edge, other);
      setTrimDrag({ id: event.id, min, max });

      const originY = e.clientY;
      const originValue = edge === 'start' ? attend.start : attend.end;

      trackDrag(
        e,
        (ev) => {
          const minutes = originValue + (ev.clientY - originY) / pxPerMinute;
          onTrim(event.id, edge, Math.min(Math.max(snapTrim(minutes), min), max));
        },
        () => setTrimDrag(null),
      );
    },
    [onTrim, pxPerMinute],
  );

  /** The same edges by keyboard, so trimming is not pointer-only. */
  const nudgeTrim = useCallback(
    (
      e: React.KeyboardEvent<HTMLDivElement>,
      event: Opportunity,
      edge: 'start' | 'end',
      attend: { start: number; end: number },
    ) => {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      e.preventDefault();
      e.stopPropagation();
      const other = edge === 'start' ? attend.end : attend.start;
      const { min, max } = trimBounds(event, edge, other);
      const from = edge === 'start' ? attend.start : attend.end;
      const step = e.key === 'ArrowUp' ? -TRIM_STEP_MINUTES : TRIM_STEP_MINUTES;
      onTrim(event.id, edge, Math.min(Math.max(from + step, min), max));
    },
    [onTrim],
  );

  /**
   * Dragging an edge of the event being written.
   *
   * Separate from `beginTrim`, which moves *attendance* inside a fixed event.
   * Here the event's own hours are what move — the thing does not exist yet, so
   * there is nothing for attendance to be a part of.
   */
  const beginDraftDrag = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, edge: 'start' | 'end') => {
      if (!draft) return;
      e.preventDefault();
      e.stopPropagation();

      const originY = e.clientY;
      const from = edge === 'start' ? draft.startMinutes : draft.endMinutes;
      const opposite = edge === 'start' ? draft.endMinutes : draft.startMinutes;

      trackDrag(e, (ev) => {
        const minutes = snapTrim(from + (ev.clientY - originY) / pxPerMinute);
        // Never let the two edges cross, or invert the block.
        if (edge === 'start') {
          onDraftChange({ ...draft, startMinutes: Math.min(minutes, opposite - SLOT_MINUTES) });
        } else {
          onDraftChange({ ...draft, endMinutes: Math.max(minutes, opposite + SLOT_MINUTES) });
        }
      });
    },
    [draft, onDraftChange, pxPerMinute],
  );

  /**
   * Dragging an edge of a personal event.
   *
   * Not a trim. A trim moves attendance inside an event that is running
   * whatever you do; a personal event has no published hours to be part of, so
   * the edge *is* the event and nothing bounds it but the opposite edge — the
   * user's own dinner can run to any hour they like.
   */
  const beginPersonalDrag = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, event: Opportunity, edge: 'start' | 'end') => {
      e.preventDefault();
      e.stopPropagation();

      const originY = e.clientY;
      const from = edge === 'start' ? fullStart(event) : fullEnd(event);
      // The other edge stays put, so it can be read once here.
      const fixed = edge === 'start' ? fullEnd(event) : fullStart(event);

      trackDrag(e, (ev) => {
        const minutes = snapTrim(from + (ev.clientY - originY) / pxPerMinute);
        if (edge === 'start') {
          onRetimePersonal(event.id, Math.min(minutes, fixed - TRIM_STEP_MINUTES), fixed);
        } else {
          onRetimePersonal(event.id, fixed, Math.max(minutes, fixed + TRIM_STEP_MINUTES));
        }
      });
    },
    [onRetimePersonal, pxPerMinute],
  );

  const nudgePersonal = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>, event: Opportunity, edge: 'start' | 'end') => {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      e.preventDefault();
      e.stopPropagation();
      const step = e.key === 'ArrowUp' ? -TRIM_STEP_MINUTES : TRIM_STEP_MINUTES;
      const start = fullStart(event);
      const end = fullEnd(event);
      if (edge === 'start') {
        onRetimePersonal(event.id, Math.min(start + step, end - TRIM_STEP_MINUTES), end);
      } else {
        onRetimePersonal(event.id, start, Math.max(end + step, start + TRIM_STEP_MINUTES));
      }
    },
    [onRetimePersonal],
  );

  const nudgeDraft = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>, edge: 'start' | 'end') => {
      if (!draft || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
      e.preventDefault();
      const step = e.key === 'ArrowUp' ? -TRIM_STEP_MINUTES : TRIM_STEP_MINUTES;
      if (edge === 'start') {
        onDraftChange({
          ...draft,
          startMinutes: Math.min(draft.startMinutes + step, draft.endMinutes - SLOT_MINUTES),
        });
      } else {
        onDraftChange({
          ...draft,
          endMinutes: Math.max(draft.endMinutes + step, draft.startMinutes + SLOT_MINUTES),
        });
      }
    },
    [draft, onDraftChange],
  );

  /**
   * Opening a day lands on the first thing in it.
   *
   * The column starts at 5am and most days start at nine, so the page opened
   * onto four empty hours of sky and every visit began by scrolling past them.
   * This brings the first event to just under the day's own header instead —
   * once per day opened, never while the reader is moving around inside it.
   */
  const firstStart = plan.timed[0] ? startMinute(plan.timed[0].event) : null;

  /** The phone's pinned date bar. Whatever it covers is not part of the view. */
  const pinned = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const node = column.current;
    // A neighbour drawn for the swipe, or a day reached by swiping, leaves the
    // page where it is: DaySwipe has already lined the hours up. A mini day is
    // not part of the page's scroll at all.
    if (!mobile || !node || preview || !landOnOpen || mini) return;

    // Arrived by a tap on the week: land on the first thing in the day.
    if (firstStart == null) return;
    // Measured in a layout effect rather than on the next frame: the DOM is
    // committed by now, and a frame that never comes — a page opened in a
    // background tab — would leave the day sitting at five in the morning.
    const delta = node.getBoundingClientRect().top + offset(firstStart) - viewTop(node, pinned.current) - 24;
    scrollHostBy(node, delta, true);
    // The day, not the scale: pinching must not throw the reader back to nine.
    // `preview` too, so a neighbour tapped open from the week lands like any day.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayKey, mobile, preview]);

  /**
   * Opened from a mini day: the hours it was showing go in the middle of the
   * view, instead of the first event. After the landing above, so this wins.
   */
  useLayoutEffect(() => {
    if (landAt == null || mini || preview) return;
    if (mobile) {
      const node = column.current;
      if (!node) return;
      const middle = (viewTop(node, pinned.current) + window.innerHeight) / 2;
      scrollHostBy(node, node.getBoundingClientRect().top + offset(landAt) - middle);
      return;
    }
    const node = scroller.current;
    if (node) node.scrollTop = Math.max(0, offset(landAt) - node.clientHeight / 2);
    // Once per arrival: the scale changing afterwards is the reader's doing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landAt, dayKey]);

  /**
   * Marks the date bar once it is pinned, and publishes its height.
   *
   * Pinned, it squares its corners and casts a shadow onto the day passing
   * underneath. The height goes to the cards, whose own titles pin just below
   * the bar instead of sliding behind it.
   */
  useLayoutEffect(() => {
    const head = pinned.current;
    const wrap = head?.parentElement;
    if (!mobile || !head || !wrap) return;

    // The bar is the day's first child, so any distance from the day's top
    // edge is the distance it has been held back while the day scrolled on.
    const check = () => {
      // The listener is on the document, so the account page's own scrolling
      // reaches it too while that page is drawn over the app.
      if (isAccountPath(window.location.pathname)) return;
      const stuck = String(head.getBoundingClientRect().top - wrap.getBoundingClientRect().top > 0.5);
      if (head.dataset.stuck !== stuck) head.dataset.stuck = stuck;
    };
    const measure = () => wrap.style.setProperty('--day-head', `${head.offsetHeight}px`);
    check();
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(head);
    // Capture, because the scrolling is done by My Schedule's box, not the page.
    document.addEventListener('scroll', check, { capture: true, passive: true });
    return () => {
      observer.disconnect();
      document.removeEventListener('scroll', check, { capture: true });
    };
  }, [mobile]);


  const { full, weekday } = longDate(dayKey);

  if (mini) {
    /**
     * What the window is centred on: the event it is shown for, or failing
     * that everything scheduled that day, first start to last end. At least
     * `windowHours` of day, or more for a long event or a full day, so there
     * are always two hours to see either side.
     */
    const focus =
      previewEvent && !previewEvent.isAllDay && startMinute(previewEvent) != null ? previewEvent : null;
    const spans = (focus ? [focus] : plan.timed.map((t) => t.event)).map((e) => attendedSpan(e, trims));
    const span = spans.length
      ? { start: Math.min(...spans.map((x) => x.start)), end: Math.max(...spans.map((x) => x.end)) }
      : { start: 9 * 60, end: 10 * 60 };
    const perHour = columnHeight / (SPAN_MIN / 60);
    const hoursShown =
      windowCentre != null
        ? windowHours
        : Math.max(windowHours, (span.end - span.start) / 60 + 2 * MINI_MARGIN_HOURS);
    const windowHeight = hoursShown * perHour;
    const middle = windowCentre ?? (span.start + span.end) / 2;
    const shift = windowHeight / 2 - offset(middle);
    // Labels stand about 16px tall: keep the ones at least half that inside.
    const clear = (8 / perHour) * 60;
    const labelled: [number, number] = [middle - hoursShown * 30 + clear, middle + hoursShown * 30 - clear];
    const listed = new Set(events.map((e) => e.id));

    return (
      <MiniWindow
        role="group"
        aria-label={`${weekday}, ${full}: your schedule${previewEvent ? ' around this event' : ''}`}
        style={{ height: windowHeight, cursor: onOpenDay ? 'pointer' : undefined }}
        title={onOpenDay ? `Open ${weekday} on My Schedule` : undefined}
        // An event, its ✕ or a journey keeps its own click; anywhere else opens the day.
        onClick={
          onOpenDay
            ? (e) => {
                if ((e.target as Element).closest('button, a, [role="button"], [data-mini-card]')) return;
                onOpenDay(dayKey, middle);
              }
            : undefined
        }
      >
        <MiniTrack
          style={{
            transform: `translateY(${shift}px)`,
            transition: glide ? `transform ${MINI_GLIDE_MS}ms ease` : undefined,
          }}
        >
          <Column style={{ height: columnHeight }}>
            <NightCap aria-hidden="true" style={{ bottom: '100%', height: windowHeight, background: NIGHT_BEFORE }} />
            <NightCap aria-hidden="true" style={{ top: '100%', height: windowHeight, background: NIGHT_AFTER }} />
            <SkyBackdrop mini />
            <HourGrid />
            <HourGutter mini within={labelled} />

            <Stage>
              {placed.map((entry) => {
                if (entry.item.kind === 'leg') {
                  const leg = legsById.get(entry.item.id);
                  if (!leg) return null;
                  return (
                    <TravelBlock
                      key={leg.id}
                      mini
                      leg={leg}
                      geometry={{ offset, scale: pxPerMinute }}
                      lane={laneOf(entry)}
                    />
                  );
                }

                const { event } = entry.item;
                const { personal, attend, overlapping, lane, windowTop, windowHeight: publishedHeight, cardTop, height } =
                  placeEvent(entry, event);
                const box = { top: cardTop, height, left: lane.left, width: lane.width };
                const isFocus = focus?.id === event.id;

                // Not yet on the schedule: a proposal, not a card.
                if (isFocus && !listed.has(event.id)) {
                  const pad = height < 34 ? 1 : 6;
                  const lines = Math.floor((height - 2 * (pad + 2.5)) / 13.4);
                  return (
                    <FocusBlock key={event.id} aria-current="true" style={{ ...box, padding: `${pad}px 8px` }}>
                      {lines >= 1 && <FocusTitle style={{ WebkitLineClamp: lines }}>{event.name}</FocusTitle>}
                    </FocusBlock>
                  );
                }

                /**
                 * The full card's cuts, at this card's size: `bare` has no room
                 * for a line of text and leaves the name to the tooltip, `short`
                 * gives the title one line and the card its tight padding.
                 */
                const bare = height < 17 || entry.lanes >= 5;
                const short = height < 30;
                const titleLines = height < 40 ? 1 : height < 80 ? 2 : 3;
                const hours = attend.trimmed
                  ? `${formatTime(attend.start)} – ${formatTime(attend.end)}`
                  : formatTimeRange(event);
                const slot = timeSlotOf(event);
                // Whole lines only, in the order they give way: a line cut
                // through by the card's foot reads as a rendering fault.
                const lineRoom = Math.floor((height - (short ? 6 : 10) - titleLines * 11.8) / 12.8);
                const conflictLine = overlapping && !isFocus;
                const placeRoom = lineRoom - (conflictLine ? 1 : 0);
                const goals = event.goals.slice(0, 3);
                const intentSize =
                  entry.lanes === 1 && !bare && !short && goals.length > 0
                    ? Math.min(14, Math.floor((height - 10 - (goals.length - 1) * 3) / goals.length))
                    : 0;
                const rail = intentSize >= 9 ? intentSize + 6 : 0;
                // Itself, or someone's own event: nothing here to open.
                const inert = isFocus || personal;
                // Anything on the schedule can come off it from here, the callout's own
                // event included. Not someone's own event: deleting one asks first, on My Schedule.
                const removable = Boolean(onRemoveProp) && listed.has(event.id) && !personal && !bare;

                return (
                  <Fragment key={event.id}>
                    {attend.trimmed && (
                      <EventWindow
                        aria-hidden="true"
                        style={{
                          top: windowTop,
                          height: publishedHeight,
                          left: lane.left,
                          width: lane.width,
                          borderRadius: 5,
                          borderWidth: 1.5,
                        }}
                      />
                    )}
                    <Slot
                      // Its own card, ringed: white on the sky outside it,
                      // since white inside a parchment card barely shows.
                      style={
                        isFocus
                          ? { ...box, outline: `2.5px solid ${FOCUS_RING}`, outlineOffset: 1.5, borderRadius: 6 }
                          : box
                      }
                    >
                      <Tooltip
                        title={bare ? `${hours} · ${event.name}` : ''}
                        placement="top"
                        arrow
                        disableHoverListener={!bare}
                      >
                        <Card
                          $mini
                          $saved={savedIds.has(event.id)}
                          $plain={personal}
                          $tight={short}
                          $fill={personal ? null : colorFor(event, colorBy)}
                          // Room for the ✕ as well as the marks beside it.
                          $rail={rail + (removable ? 12 : 0)}
                          data-mini-card
                          aria-current={isFocus || undefined}
                          aria-disabled={inert || undefined}
                          tabIndex={inert ? -1 : undefined}
                          onClick={inert ? undefined : () => onOpenEvent(event)}
                          style={{
                            cursor: inert ? 'default' : undefined,
                            boxShadow: overlapping && !isFocus ? `inset 0 0 0 1.5px ${CONFLICT}` : undefined,
                          }}
                        >
                          {!bare && (
                            <>
                              <MiniTitle style={{ WebkitLineClamp: titleLines }}>{event.name}</MiniTitle>
                              {/* Straight under the title, so a card an hour
                                  long still says it; the place and hours give
                                  way first. */}
                              {conflictLine && lineRoom >= 1 && (
                                <MiniLine style={{ color: CONFLICT }}>
                                  <WarningGlyph size={9} />
                                  <span>Conflict</span>
                                </MiniLine>
                              )}
                              {placeRoom >= 1 && (
                                <MiniLine>
                                  <MiniIcon>
                                    <LocationPin />
                                  </MiniIcon>
                                  <span>{placeText(event)}</span>
                                </MiniLine>
                              )}
                              {placeRoom >= 2 && (
                                <MiniLine>
                                  <MiniIcon>{slot && <Icon name={`time:${slot}`} size={9} />}</MiniIcon>
                                  <span>{hours}</span>
                                </MiniLine>
                              )}
                            </>
                          )}
                        </Card>
                      </Tooltip>
                      {removable && (
                        <MiniRemove
                          aria-label={`Remove ${event.name} from My Schedule`}
                          title="Remove from My Schedule"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            onRemove(event.id);
                          }}
                        >
                          ✕
                        </MiniRemove>
                      )}
                      {rail > 0 && (
                        <IntentRail
                          // Beside the ✕, as the full card's are.
                          style={{ top: 5, right: removable ? 19 : 6, gap: 3, pointerEvents: 'none' }}
                        >
                          {goals.map((goal) => (
                            <Box key={goal.key} component="span" sx={{ display: 'flex' }}>
                              <Icon name={`goal:${goal.key}`} size={intentSize} />
                            </Box>
                          ))}
                        </IntentRail>
                      )}
                    </Slot>
                  </Fragment>
                );
              })}
            </Stage>
          </Column>
        </MiniTrack>
      </MiniWindow>
    );
  }

  const body = (
    <>
    <Wrap>
      <DayHeading ref={pinned} $pinned={mobile}>
      <Head>
        <Typography
          sx={{
            fontFamily: FONTS.serif,
            fontWeight: 700,
            fontSize: mobile ? 25 : 26,
            whiteSpace: 'nowrap',
          }}
        >
          {full}
        </Typography>
        {/* Weekday sits immediately right of the date, 30px away. */}
        <Typography
          sx={{
            fontFamily: FONTS.serif,
            fontSize: mobile ? 16 : 19,
            opacity: 0.8,
            ml: mobile ? 0 : '30px',
          }}
        >
          {weekday}
        </Typography>

        <Box sx={{ flex: 1 }} />

        {/* On a phone the counts move to their own line below, where they can
            be read; the head is already full at that width. */}
        {!mobile && (
          <Typography sx={{ fontSize: 12, opacity: 0.6 }}>
            {plan.timed.length + plan.allDay.length} scheduled
            {plan.overlaps.length > 0 && (
              <Box
                component="span"
                sx={{ color: CONFLICT, fontFamily: FONTS.body, fontWeight: 700, opacity: 1 }}
              >
                {' · '}
                {plan.overlaps.length}{' '}
                {plan.overlaps.length === 1 ? 'conflict' : 'conflicts'}
              </Box>
            )}
            {plan.tight.length > 0 && ` · ${plan.tight.length} tight`}
          </Typography>
        )}

        {/* The phone's day closes from its own corner. Tapping the ringed
            column again does it too, but nothing on screen said so. */}
        <IconButton
          onClick={onClose}
          aria-label={mobile ? 'Close this day' : 'Back to the week'}
          sx={{ color: BRAND.parchment, ...(mobile ? { ml: 'auto', p: 0.5 } : null) }}
        >
          ✕
        </IconButton>
      </Head>

      {mobile && (
        <Typography sx={{ fontSize: 13, opacity: 0.65, padding: '0 16px 10px' }}>
          {plan.timed.length + plan.allDay.length} scheduled
          {plan.overlaps.length > 0 && (
            <Box
              component="span"
              sx={{ color: CONFLICT, fontFamily: FONTS.body, fontWeight: 700, opacity: 1 }}
            >
              {'   '}
              {plan.overlaps.length} {plan.overlaps.length === 1 ? 'conflict' : 'conflicts'}
            </Box>
          )}
          {plan.tight.length > 0 && (
            <Box component="span" sx={{ ml: 1.4, fontWeight: 700 }}>
              {plan.tight.length} tight {plan.tight.length === 1 ? 'connection' : 'connections'}
            </Box>
          )}
        </Typography>
      )}
      </DayHeading>

      <Scroller
        ref={scroller}
        $inline={mobile}
        // Room under the hours, so a late event can reach the middle of the
        // space above the callout, however tall it measured.
        style={calloutPeek && !mobile ? { paddingBottom: `${HALF_MAX * 100}vh` } : undefined}
      >
        <Track style={mobile ? { paddingLeft: 44, paddingRight: 8, paddingBottom: 28 } : undefined}>

          {/* All-day events have no position on the clock, so they sit above it. */}
          {plan.allDay.length > 0 && (
            <AllDayStrip>
              {plan.allDay.map((e) => (
                <Box key={e.id} sx={{ position: 'relative' }}>
                  <Card
                    $saved={savedIds.has(e.id)}
                    $fill={colorFor(e, colorBy)}
                    onClick={(click) => {
                      // Writing an event on a phone: the day is for looking at.
                      if (mobile && draft) return;
                      onOpenEvent(e, 'day');
                      centerAboveCallout(click.currentTarget);
                    }}
                    style={{ height: 'auto', boxShadow: 'inset 0 0 0 1px rgba(54,47,42,0.35)' }}
                  >
                    <Flag>all day · no time published</Flag>
                    <Typography sx={{ fontFamily: FONTS.serif, fontSize: 17, fontWeight: 700, mt: 0.6 }}>
                      {e.name}
                    </Typography>
                    {!mobile && <CardTags event={e} />}
                    <Meta style={{ marginTop: 7 }}>
                      <MetaIcon>
                        <LocationPin />
                      </MetaIcon>
                      <LocationLine event={e} />
                    </Meta>
                  </Card>
                  <Tooltip title="Remove from Calendar" placement="top" arrow>
                    <Remove
                      aria-label={`Remove ${e.name}`}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        onRemove(e.id);
                      }}
                    >
                      ✕
                    </Remove>
                  </Tooltip>
                  {/* The phone reads which list it is in; it changes lists in
                      the callout, as it does for every other card here. */}
                  {mobile ? (
                    <ListMarkFor event={e} saved={savedIds.has(e.id)} />
                  ) : (
                    <ListToggleButton
                      event={e}
                      saved={savedIds.has(e.id)}
                      onToggle={onToggleList}
                    />
                  )}
                </Box>
              ))}
            </AllDayStrip>
          )}

          <Column ref={column} data-day-column style={{ height: columnHeight }}>
            {/* Sky, hour rules and gutter share the column's coordinate space. */}
            <SkyBackdrop />
            <HourGrid />
            <HourGutter />

            {plan.timed.length === 0 && plan.allDay.length === 0 && (
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.95)',
                  textAlign: 'center',
                  pt: 10,
                  fontFamily: FONTS.serif,
                  fontSize: 18,
                  textShadow: '0 1px 4px rgba(0,0,0,0.4)',
                }}
              >
                Nothing scheduled on this day yet.
              </Typography>
            )}

            {/**
             * Empty schedule is a control too: clicking it starts an event in
             * the half-hour that was clicked. Behind everything else in the
             * column, so a click only reaches it where there is nothing.
             */}
            <SlotCatcher
              onClick={(e) => {
                const box = e.currentTarget.getBoundingClientRect();
                const minutes = DAY_START_MIN + (e.clientY - box.top) / pxPerMinute;
                onEmptySlot(dayKey, Math.min(Math.max(minutes, DAY_START_MIN), DAY_END_MIN - 30));
              }}
              aria-label="Add a personal event at this time"
            />

            {/* Inset from the column, so sky shows either side of the cards. */}
            <Stage>
              {draft && draft.dayKey === dayKey && (
                <DraftBlock
                  ref={draftBlock}
                  style={{
                    top: offset(draft.startMinutes),
                    height: Math.max(offset(draft.endMinutes) - offset(draft.startMinutes), 24),
                    ...(mobile && phoneDraft),
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {mobile && offset(draft.endMinutes) - offset(draft.startMinutes) < 48 ? (
                    // Too short for two lines on a phone: the title first, then the hours.
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0, whiteSpace: 'nowrap' }}>
                      <Typography
                        sx={{
                          fontFamily: FONTS.serif,
                          fontSize: 15,
                          fontWeight: 700,
                          lineHeight: 1.16,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          opacity: draft.title.trim() ? 1 : 0.55,
                        }}
                      >
                        {draft.title.trim() || 'New Event'}
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, fontWeight: 700, flexShrink: 0 }}>
                        {formatTime(draft.startMinutes)} – {formatTime(draft.endMinutes)}
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <Typography sx={{ fontSize: 11.5, fontWeight: 700 }}>
                        {formatTime(draft.startMinutes)} – {formatTime(draft.endMinutes)}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: FONTS.serif,
                          fontSize: 15,
                          fontWeight: 700,
                          lineHeight: 1.16,
                          opacity: draft.title.trim() ? 1 : 0.55,
                        }}
                      >
                        {draft.title.trim() || (mobile ? 'New Event' : 'New event')}
                      </Typography>
                    </>
                  )}

                  {!mobile && (['start', 'end'] as const).map((edge) => (
                    <DraftHandle
                      key={edge}
                      $edge={edge}
                      role="slider"
                      tabIndex={0}
                      aria-label={edge === 'start' ? 'Start time' : 'End time'}
                      aria-valuetext={formatTime(
                        edge === 'start' ? draft.startMinutes : draft.endMinutes,
                      )}
                      onPointerDown={(ev) => beginDraftDrag(ev, edge)}
                      onKeyDown={(ev) => nudgeDraft(ev, edge)}
                    />
                  ))}
                </DraftBlock>
              )}
            {placed.map((entry) => {
              if (entry.item.kind === 'leg') {
                const leg = legsById.get(entry.item.id);
                if (!leg) return null;
                return (
                  <TravelBlock
                    key={leg.id}
                    leg={leg}
                    geometry={{ offset, scale: pxPerMinute }}
                    lane={laneOf(entry)}
                    // A phone taps the block to change how it is travelling;
                    // dragging the departure is a desktop's precision.
                    onDrag={mobile ? undefined : onLegDrag}
                    onOpen={(l) => setOpenLegId(l.id)}
                  />
                );
              }

              const { event } = entry.item;
              const { personal, attend, overlapping, lane, windowTop, windowHeight, cardTop, height } =
                placeEvent(entry, event);
              const total = entry.lanes;
              const dragging = trimDrag?.id === event.id ? trimDrag : null;

              /**
               * Three cuts, and they are not the same question.
               *
               * `dense` drops the secondary furniture — tags, flags, the RSVP
               * line — once the card is short or shares its row.
               *
               * `short` is about height: a 30-minute block is 56px, and the
               * usual padding plus a two-line title does not fit in it.
               *
               * `narrow` is about width. Below roughly a fifth of the column a
               * title is not truncated so much as destroyed, and rendering two
               * syllables of it is worse than rendering none: it reads as a
               * bug. Its buttons would cover the whole card, so they go too.
               *
               * `bare` is either cut taken to the end: only the time is left,
               * and the title moves to the tooltip. The block's position,
               * length and colour still say everything this view exists to say.
               */
              const short = height < SHORT_CARD_H;
              const narrow = total >= 5;
              const dense = height < 128 || total > 1 || short;
              const bare = narrow || height < TINY_CARD_H;

              /**
               * The hours the user is there for, not the hours published: the
               * block is drawn for the attended span, and the time on it should
               * agree. When those differ, the note beside the time says by how
               * much, which is the only statement that the block is shorter
               * than the event.
               */
              const hours = attend.trimmed
                ? `${formatTime(attend.start)} – ${formatTime(attend.end)}`
                : formatTimeRange(event);
              const trimNote = attend.trimmed ? trimNoteFor(attend.lateBy, attend.earlyBy) : '';
              const slot = timeSlotOf(event);
              /**
               * One gap for both: the space under the title and the space
               * under the place. They used to differ by a point and a half,
               * which on a card this size reads as the hours having drifted
               * away from the rest of it.
               */
              const metaGap = dense ? 4 : 7;

              // At most three marks: past that the stack outgrows the card and
              // the fourth says little the first three have not.
              const goals = event.goals.slice(0, 3);
              /**
               * The marks are the first thing a phone gives up.
               *
               * They sit in a rail the text has to clear, and on a 375px screen
               * a card sharing its row with another has about forty pixels of
               * width left after it — which is a title nobody can read beside
               * an icon nobody asked for.
               */
              const intentSize =
                bare || (mobile && total > 1)
                  ? 0
                  : intentIconSize(total, height, goals.length, mobile);
              // Personal events have no intents, so the rail never shows there.
              // Text clears the button column, plus the marks when they show.
              const railWidth = intentSize >= 13 ? intentSize + 10 : 0;

              return (
                <Fragment key={event.id}>
                  {/* Only when it says something the card does not. */}
                  {attend.trimmed && (
                    <EventWindow
                      aria-hidden="true"
                      style={{
                        top: windowTop,
                        height: windowHeight,
                        left: lane.left,
                        width: lane.width,
                      }}
                    />
                  )}

                  {dragging && (
                    <TrimGhost
                      aria-hidden="true"
                      style={{
                        top: offset(dragging.min),
                        height: Math.max(offset(dragging.max) - offset(dragging.min), 8),
                        left: lane.left,
                        width: lane.width,
                      }}
                    />
                  )}

                <Slot
                  style={{ top: cardTop, height, left: lane.left, width: lane.width }}
                >
                  <Tooltip
                    title={bare ? `${hours} · ${event.name}` : ''}
                    placement="top"
                    arrow
                    disableHoverListener={!bare}
                  >
                  <Card
                    $saved={savedIds.has(event.id)}
                    /**
                     * A personal event is not part of the programme, so it does
                     * not wear the programme's clothes: square corners rather
                     * than a ticket's notches, and always parchment — the
                     * colour lenses sort the week's events by what the dataset
                     * knows about them, and this one has none of that.
                     */
                    $plain={personal}
                    $tight={short}
                    $compact={mobile}
                    $fill={personal ? null : colorFor(event, colorBy)}
                    $ghost={previewEvent?.id === event.id}
                    $hidden={revealingId === event.id && !revealed}
                    $rail={railWidth}
                    onClick={(click) => {
                      // Writing an event on a phone: the day is for looking at.
                      if (mobile && draft) return;
                      if (personal) {
                        onEditPersonal(event);
                        return;
                      }
                      onOpenEvent(event, 'day');
                      centerAboveCallout(click.currentTarget);
                    }}
                    style={overlapping ? { boxShadow: `inset 0 0 0 2px ${CONFLICT}` } : undefined}
                  >
                    <CardContent>
                      {/**
                       * One line only: give it the title.
                       *
                       * The name is the thing you cannot infer from where the
                       * block sits, so the name is what the line carries. The
                       * hours are in the tooltip and in the callout.
                       */}
                      {bare ? (
                        <Typography
                          sx={{
                            fontFamily: FONTS.serif,
                            fontSize: 12,
                            fontWeight: 700,
                            lineHeight: 1.2,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {event.name}
                        </Typography>
                      ) : (
                        <>
                          {/**
                           * Title, then where, then when: the order a card
                           * gives things up in as it gets shorter, so the
                           * last to go is the name and the first is the hours,
                           * which the block's position already roughly says.
                           */}
                          <Typography
                            sx={{
                              fontFamily: FONTS.serif,
                              fontSize: dense ? 14.5 : 16,
                              fontWeight: 700,
                              lineHeight: 1.18,
                              display: '-webkit-box',
                              WebkitLineClamp: short ? 1 : dense ? 2 : 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {event.name}
                          </Typography>

                          {/**
                           * Place over hours, sharing one icon column, with the
                           * same row gap the title has above them: three lines
                           * of one block, rather than a title and two strays.
                           */}
                          <Meta style={{ marginTop: metaGap, rowGap: metaGap }}>
                            <MetaIcon>
                              <LocationPin />
                            </MetaIcon>
                            <LocationLine event={event} />

                            <MetaIcon>
                              {slot && <Icon name={`time:${slot}`} size={12} />}
                            </MetaIcon>
                            {/* The note sits to the right of the hours, wrapping beside
                                them if it must; a card too narrow for that puts it below. */}
                            <Box sx={{ containerType: 'inline-size', minWidth: 0 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  alignItems: 'baseline',
                                  columnGap: 0.8,
                                  rowGap: 0.2,
                                  '@container (min-width: 240px)': {
                                    flexWrap: 'nowrap',
                                    '& > span': { minWidth: 0 },
                                  },
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontFamily: FONTS.body,
                                    fontSize: 11.5,
                                    fontWeight: 700,
                                    letterSpacing: '0.02em',
                                    lineHeight: 1.2,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {hours}
                                </Typography>
                                {trimNote && <TrimNote>{trimNote}</TrimNote>}
                              </Box>
                            </Box>
                          </Meta>

                          {!dense && (!hasEndTime(event) || (savedIds.has(event.id) && !mobile)) && (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                              {!hasEndTime(event) && (
                                <Tooltip title="No end time was published — we assumed one hour.">
                                  <Flag>end time not listed</Flag>
                                </Tooltip>
                              )}
                              {/* The phone says this with the mark under the ✕. */}
                              {savedIds.has(event.id) && !mobile && <Flag>saved for later</Flag>}
                            </Box>
                          )}

                          {overlapping && (
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.7,
                                color: CONFLICT,
                                fontWeight: 700,
                                fontSize: 11,
                                mt: 0.4,
                              }}
                            >
                              <WarningGlyph />
                              <span style={{ marginLeft: 2 }}>Conflict</span>
                            </Box>
                          )}

                          {/* Never on a phone: four tag pills under the hours
                              is the card's own width again, and a day of them
                              reads as a pile rather than a schedule. */}
                          {!dense && !mobile && <CardTags event={event} />}

                          {!dense && (event.rsvpType === 'invite_only' || event.rsvpType === 'apply_to_attend') && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.9, color: CONFLICT }}>
                              <WarningGlyph />
                              <Typography sx={{ fontSize: 11.5, fontWeight: 700 }}>{rsvpLabel(event.rsvpType)}</Typography>
                            </Box>
                          )}
                        </>
                      )}
                      {revealingId === event.id && revealed && <Confetti trigger={revealBurst} />}
                    </CardContent>
                  </Card>
                  </Tooltip>

                  {/* The two buttons are hidden on a narrow card: at that
                      width they would cover the whole thing. The callout still
                      has both, one click away. */}
                  {!narrow && (
                    <>
                      <Tooltip
                        title={personal ? 'Delete this event' : 'Remove from Calendar'}
                        placement="top"
                        arrow
                      >
                        <Remove
                          aria-label={`Remove ${event.name}`}
                          style={short ? { top: 2, right: 6 } : undefined}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            onRemove(event.id);
                          }}
                        >
                          ✕
                        </Remove>
                      </Tooltip>
                      {/* A personal event is in no list, so there is nothing to
                          move it to; a short card has no room for the second
                          button under the ✕. */}
                      {/* The phone keeps the ✕ and drops the list toggle: two
                          20px targets stacked in a corner is one target. */}
                      {!personal && !short && !mobile && (
                        <ListToggleButton
                          event={event}
                          saved={savedIds.has(event.id)}
                          onToggle={onToggleList}
                        />
                      )}

                      {!personal && !short && mobile && (
                        <ListMarkFor event={event} saved={savedIds.has(event.id)} />
                      )}

                      {intentSize >= 13 && (
                        <IntentRail style={{ right: 10 + BUTTON_COLUMN }}>
                          {goals.map((goal) => (
                            <Tooltip
                              key={goal.key}
                              title={goalTooltip(goal)}
                              placement="top"
                              arrow
                            >
                              <Box component="span" sx={{ display: 'flex' }}>
                                <Icon name={`goal:${goal.key}`} size={intentSize} />
                              </Box>
                            </Tooltip>
                          ))}
                        </IntentRail>
                      )}
                    </>
                  )}
                  {/**
                   * Both edges, revealed by hovering the card. On a published
                   * event they move attendance inside its hours; on a personal
                   * one they move the event itself.
                   *
                   * Drawn whatever the card's size, unlike the buttons: they
                   * sit on the card's edges rather than inside it, and a card
                   * trimmed down to fifteen minutes is exactly the one that
                   * most needs its handles back.
                   */}
                  {!mobile &&
                    (['start', 'end'] as const).map((edge) => (
                    <TrimHandle
                      key={edge}
                      data-trim
                      $edge={edge}
                      role="slider"
                      tabIndex={0}
                      aria-label={
                        personal
                          ? edge === 'start'
                            ? `Start of ${event.name}`
                            : `End of ${event.name}`
                          : edge === 'start'
                            ? `When you arrive at ${event.name}`
                            : `When you leave ${event.name}`
                      }
                      aria-valuemin={personal ? undefined : fullStart(event)}
                      aria-valuemax={personal ? undefined : fullEnd(event)}
                      aria-valuenow={edge === 'start' ? attend.start : attend.end}
                      aria-valuetext={formatTime(edge === 'start' ? attend.start : attend.end)}
                      onPointerDown={(ev) =>
                        personal
                          ? beginPersonalDrag(ev, event, edge)
                          : beginTrim(ev, event, edge, attend)
                      }
                      onKeyDown={(ev) =>
                        personal
                          ? nudgePersonal(ev, event, edge)
                          : nudgeTrim(ev, event, edge, attend)
                      }
                      onClick={(ev) => ev.stopPropagation()}
                    />
                  ))}
                </Slot>
                </Fragment>
              );
            })}
            </Stage>
          </Column>
        </Track>
      </Scroller>
    </Wrap>

      {openLeg && (
        <TravelModeDialog
          open
          from={openLeg.estimate.from}
          to={openLeg.estimate.to}
          unlocated={[openLeg.from, openLeg.to].filter((e) => !isMappable(e)).map((e) => e.name)}
          options={travelOptions(openLeg.from, openLeg.to)}
          selected={openLeg.estimate.mode}
          windowMinutes={openLeg.windowEnd - openLeg.windowStart}
          departure={
            mobile
              ? {
                  startMinutes: openLeg.startMinutes,
                  ...travelBounds(
                    openLeg.windowStart,
                    openLeg.windowEnd,
                    openLeg.estimate.minutes,
                  ),
                  minutes: openLeg.estimate.minutes,
                  onChange: (start) => onLegDrag(openLeg.id, start),
                }
              : undefined
          }
          mapsUrl={directionsUrl(
            mapsTarget(openLeg.from),
            mapsTarget(openLeg.to),
            openLeg.estimate.mode,
          )}
          onSelect={onModeChange}
          onClose={() => setOpenLegId(null)}
        />
      )}
    </>
  );

  // The phone's day sits in the page under the week. DaySwipe draws the days
  // either side of it and moves between them.
  if (mobile) return <InlineDay>{body}</InlineDay>;

  return (
    <Overlay
      ref={overlay}
      onClick={(e) => {
        // Any click on the margin/scrim returns to the week.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Arrow $side="left" onClick={onPrev} disabled={!canPrev} aria-label="Previous day">
        <ChevronGlyph dir="left" />
      </Arrow>
      <Arrow $side="right" onClick={onNext} disabled={!canNext} aria-label="Next day">
        <ChevronGlyph dir="right" />
      </Arrow>

      <Panel ref={panel} $raised={Boolean(draft)}>
        {draft && (
          <FormPanel
            ref={formPanel}
            style={beside ? { width: formWidth } : undefined}
            onClick={(e) => e.stopPropagation()}
          >
            <PersonalEventForm
              heading={onDraftDelete ? 'Edit personal event' : 'New personal event'}
              draft={draft}
              onChange={onDraftChange}
              onSave={onDraftSave}
              onCancel={onDraftCancel}
              onDelete={onDraftDelete}
            />
          </FormPanel>
        )}
        {body}
      </Panel>
    </Overlay>
  );
}

function ChevronGlyph({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg width={17} height={17} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d={dir === 'left' ? 'M15 5 8 12l7 7' : 'M9 5l7 7-7 7'}
      />
    </svg>
  );
}

function CardTags({ event }: { event: Opportunity }) {
  if (event.topics.length === 0) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mt: 0.9 }}>
      {event.topics.slice(0, 4).map((topic) => (
        <Box key={topic.key} sx={{ width: 'auto', minWidth: 122 }}>
          <TagPill topic={topic} on size="sm" />
        </Box>
      ))}
    </Box>
  );
}

/** "Arriving 15 mins late / leaving 1 hr 30 mins early", or either half on its own. */
function trimNoteFor(lateBy: number, earlyBy: number): string {
  const parts: string[] = [];
  if (lateBy > 0) parts.push(`Arriving ${trimLength(lateBy)} late`);
  if (earlyBy > 0) parts.push(`${parts.length ? 'leaving' : 'Leaving'} ${trimLength(earlyBy)} early`);
  return parts.join(' / ');
}

/**
 * Where a schedule card says the event is.
 *
 * The borough and neighborhood when the dataset has them, and otherwise the
 * fixed label for the location type — never the street address. A card in the
 * day view is read at a glance, in a column it shares with the rest of the
 * afternoon, and an address is a line of small print that says less than
 * "Manhattan — Midtown" while taking three times the room. The callout has the
 * address, and the map.
 *
 * A personal event is the exception: its location is whatever its author typed,
 * and there is nothing else to fall back to.
 */
function placeText(event: Opportunity): string {
  if (event.neighborhood) return formatArea(event.neighborhood);
  if (isPersonal(event)) return venueText(event);
  if (event.locationType && event.locationType !== 'address') return LOCATION_LABEL[event.locationType];
  return LOCATION_LABEL.see_listing;
}

/** Small inline pin, matching the Explore cards'. */
function LocationPin() {
  return (
    <svg viewBox="0 0 24 24" width={11} height={11} aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5Z"
      />
    </svg>
  );
}

/**
 * The place and the hours, in an icon column and a text column.
 *
 * The same two marks the Explore cards carry, in the same order, so a card in
 * the day reads as the same object as the card it was scheduled from: a pin for
 * where, the time of day for when.
 */
const Meta = styled('div')({
  display: 'grid',
  gridTemplateColumns: `${META_ICON}px minmax(0, 1fr)`,
  // Each mark against the first line of its row, not the middle of it: the
  // hours carry a trim note that can wrap to four lines on a shared column,
  // and a sun floating halfway down them belongs to nothing.
  alignItems: 'start',
  columnGap: 4,
  color: BRAND.mocha,
});

const MetaIcon = styled('span')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // One text line tall, so the mark centres on the line beside it.
  height: 14,
  // The marks are furniture; the words are the card.
  opacity: 0.65,
});

function LocationLine({ event }: { event: Opportunity }) {
  return (
    <Typography
      sx={{
        fontSize: 11.5,
        fontWeight: 700,
        letterSpacing: '0.03em',
        lineHeight: 1.25,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {placeText(event)}
    </Typography>
  );
}

/**
 * What to hand Google Maps for one end of a journey.
 *
 * The street address when there is one, otherwise the neighborhood — which is
 * still a routable place, and is all the schedule itself knows about an event
 * whose venue is withheld.
 */
function mapsTarget(event: Opportunity): string | null {
  const address = mapsQueryOf(event);
  if (address) return address;
  return event.neighborhood ? `${event.neighborhood.replace(' - ', ', ')}, New York` : null;
}
